import {ApiResponse, BaseHttpService, Company} from '@ems/api';
import { Injectable } from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { HttpCacheManager } from '@ngneat/cashew';

const expandParams = {
  'expand[0]': 'mailingAddress',
  'expand[1]': 'visitingAddress',
  'expand[2]': 'billingAddress',
  'expand[4]': 'parent',
  'expand[5]': 'subsidiaries',
  'expand[6]': 'billingDetails',
  'expand[7]': 'updatedBy',
  'expand[8]': 'sections',
  'expand[9]': 'keyAccountsSug',
  'expand[10]': 'keyAccountsEms',
}

@Injectable({providedIn: 'root'})
export class CompanyService extends BaseHttpService {
  override cacheEnabled = true;

  private companiesCache = new Map<string, Observable<Company[]>>();

  constructor(
    http: HttpClient,
    cookieService: CookieService,
    manager: HttpCacheManager,
  ) {
    super(http, cookieService, manager);
  }

  getCompany(id: number): Observable<any> {
    this.invalidateCache();
    return this.get(`company/${id}`, expandParams);
  }

  listCompanies(params: any): Observable<ApiResponse<Company[]>> {
    this.invalidateCache();
    const queryParams: Record<string, any> = {
      ...expandParams,
      ...params
    };
    return this.get('companies', queryParams);
  }

  getCompanies(
    types: string | string[],
    query?: string,
    hasSubsidiaries?: boolean,
    page = 1,
    pageSize = 20
  ): Observable<ApiResponse<Company[]>> {
    this.invalidateCache();
    const queryParams: Record<string, any> = {
      page,
      pageSize,
      'expand[0]': 'sections',
      'expand[1]': 'subsidiaries',
      ...(hasSubsidiaries ? {'filter[subsidiaries][has]': '1'} : {}),
      ...(query ? {'filter[name][like]': `%${query}%`} : {}),
      ...(Array.isArray(types)
        ? types.reduce((acc: any, curr: string, i: number) => {
            acc[`filter[business_relationship][in][${i}]`] = curr;
            return acc;
          }, {})
        : {}
      ),
      ...(typeof types === 'string' ? {'filter[business_relationship][eq]': types} : {}),
    };

    return this.get('companies', queryParams);
  }

  getAllCompanies(
    types: string | string[],
    parentId?: number,
    pageSize = 20
  ): Observable<Company[]> {
    const cacheKey = `${JSON.stringify(types)}_${parentId}_${pageSize}`;
    if (!this.companiesCache.has(cacheKey)) {
      const result$ = this._fetchAllCompanies(types, parentId, pageSize).pipe(
        shareReplay(1)
      );
      this.companiesCache.set(cacheKey, result$);
    }
    return this.companiesCache.get(cacheKey)!;
  }

  invalidateCompaniesCache(): void {
    this.companiesCache.clear();
  }

  private _fetchAllCompanies(
    types: string | string[],
    parentId?: number,
    pageSize = 20
  ): Observable<Company[]> {
    const queryParams: Record<string, any> = {
      pageSize,
      'expand[0]': 'sections',
      'expand[1]': 'subsidiaries',
      ...(typeof parentId === 'number' && parentId > 0 ? {'filter[parent_id][is_null]': parentId} : {}),
      ...(Array.isArray(types)
        ? types.reduce((acc: any, curr: string, i: number) => {
            acc[`filter[business_relationship][in][${i}]`] = curr;
            return acc;
          }, {})
        : {}
      ),
      ...(typeof types === 'string' ? {'filter[business_relationship][eq]': types} : {}),
    };

    return this.getAll('companies', queryParams);
  }

  getCompaniesByQuery(query?: string, page = 1, pageSize = 5, types?: string | string[]): Observable<ApiResponse<Company[]>> {
    this.invalidateCache();
    const queryParams: any = {
      'expand[0]': 'mailingAddress',
      page,
      pageSize,
      sort: '+last_name',
      ...(query ? { query } : {}),
      ...(Array.isArray(types)
          ? types.reduce((acc: any, curr: string, i: number) => {
            acc[`filter[business_relationship][in][${i}]`] = curr;
            return acc;
          }, {})
          : {}
      ),
      ...(typeof types === 'string' ? {'filter[business_relationship][eq]': types} : {}),
    };
    return this.get('companies', queryParams);
  }

  create(body: any): Observable<any> {
    const queryParams = {
      'expand[0]': 'billingAddress',
      'expand[1]': 'visitingAddress',
      'expand[2]': 'mailingAddress',
      'expand[3]': 'createdBy',
      'expand[5]': 'updatedBy',
      'expand[6]': 'billingDetails',
    };
    return this.post('companies', body, {}, queryParams);
  }

  update(id: number, body: any): Observable<any> {
    this.invalidateCache();
    const queryParams = {
      'expand[0]': 'billingAddress',
      'expand[1]': 'visitingAddress',
      'expand[2]': 'mailingAddress',
      'expand[3]': 'updatedBy',
      'expand[4]': 'billingDetails',
      'expand[5]': 'sections',
    };
    return this.post('company/' + id, body, {}, queryParams);
  }

  deleteCompany(id: number): Observable<any> {
    const params = {
      ...expandParams
    };
    return this.delete(`company/${id}`, params);
  }

  exportCompanies(body: any, params: any): Observable<HttpResponse<Blob>> {
    return this.postBlobWithResponse('companies/export', body, params || {});
  }
}
